/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { ButtonHTMLAttributes, } from "react";
import cn from "./helpers/cn";
import { ColorProps, } from "./types";

export type IconButtonProps = {
  children?: React.ReactNode;
  color?: ColorProps | "default";
  size?: "small" | "medium";
  edge?: "start" | "end" | false;
  disabled?: boolean;
  disableFocusRipple?: boolean;
  disableRipple?: boolean;
  component?: React.ElementType;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "color">;

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (props, ref) => {
    const {
      children,
      color = "default",
      size = "medium",
      edge = false,
      disabled = false,
      // Props do MUI mantidas por compatibilidade — os ripples são nativos do MUI
      disableFocusRipple: _disableFocusRipple,
      disableRipple: _disableRipple,
      component: Component = "button",
      type = "button",
      className,
      ...other
    } = props;

    const selectedColor = disabled ? "default" : color;

    const baseClasses =
      "inline-flex items-center justify-center shrink-0 rounded-full text-center transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 select-none";

    const sizeClasses = {
      small: "p-1 text-base",
      medium: "p-2 text-xl",
    };

    const colorClasses = {
      primary:
        "text-primary hover:bg-primary/10 focus-visible:outline-primary",
      secondary:
        "text-secondary hover:bg-secondary/10 focus-visible:outline-secondary",
      default:
        "text-gray-700 hover:bg-gray-500/10 focus-visible:outline-gray-500",
      inherit: "text-inherit hover:bg-current/10",
    };

    const edgeClasses = {
      start: size === "small" ? "-ml-1" : "-ml-3",
      end: size === "small" ? "-mr-1" : "-mr-3",
    };

    const isButton = Component === "button";

    return (
      <Component
        ref={ref}
        {...(isButton ? { type, disabled } : {})}
        aria-disabled={disabled || undefined}
        className={cn(
          baseClasses,
          sizeClasses[size],
          colorClasses[selectedColor],
          edge === "start" && edgeClasses.start,
          edge === "end" && edgeClasses.end,
          disabled && "opacity-50 cursor-not-allowed pointer-events-none",
          className
        )}
        {...other}
      >
        {children}
      </Component>
    );
  }
);

IconButton.displayName = "IconButton";

export default IconButton;
